import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card } from "../components/Shared";
import { useFetch } from "../components/Shared";
import { usePipeline, pipelineParams } from "../lib/pipeline";
import { toast } from "sonner";
import { Brain, ClockCounterClockwise, Stethoscope, ArrowsClockwise } from "@phosphor-icons/react";

function Stat({ label, value, suffix = "", testid }) {
  return (
    <Card className="p-5" testid={testid}>
      <div className="section-label mb-1">{label}</div>
      <div className="font-heading text-3xl font-medium tabular-nums">{value ?? 0}{suffix}</div>
    </Card>
  );
}

export default function SupervisorDashboard() {
  const { activePipelineSlug } = usePipeline();
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const { data, loading, reload } = useFetch(
    () => api.get("/supervisor/dashboard", { params: { days, ...pipelineParams(activePipelineSlug) } }).then((r) => r.data),
    [days, activePipelineSlug]
  );

  const running = job && job.status === "running";

  const poll = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data: s } = await api.get("/supervisor/backfill/status");
        setJob(s);
        if (s.status === "done" || s.status === "error" || s.status === "idle") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (s.status === "done") { toast.success(`Retrospectivo listo: ${s.analyzed} conversaciones analizadas, ${s.agents_diagnosed} agentes diagnosticados`); reload(); }
          if (s.status === "error") toast.error("El análisis retrospectivo falló");
        }
      } catch { /* */ }
    }, 3000);
  };

  useEffect(() => {
    // Al montar, reanudar polling si hay un job corriendo
    api.get("/supervisor/backfill/status").then(({ data: s }) => {
      setJob(s);
      if (s.status === "running") poll();
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  const runBackfill = async () => {
    setBusy("backfill");
    try {
      const { data: res } = await api.post("/supervisor/backfill", {
        pipeline_slug: activePipelineSlug === "all" ? null : activePipelineSlug,
        days, force: false,
      });
      if (res.already_running) toast.info("Ya hay un análisis retrospectivo en curso");
      else toast.success("Análisis retrospectivo iniciado sobre todas las conversaciones");
      setJob({ status: "running", phase: "starting", processed: 0, total: 0, analyzed: 0 });
      poll();
    } catch (e) { toast.error("No se pudo iniciar el retrospectivo"); } finally { setBusy(""); }
  };

  const runDiagnoseAll = async () => {
    setBusy("diag");
    try {
      const { data: res } = await api.post("/supervisor/diagnose-all", null, { params: { days } });
      toast.success(`Diagnóstico corrido en ${res.agents_processed} agentes`);
      reload();
    } catch (e) { toast.error("Error en diagnóstico"); } finally { setBusy(""); }
  };

  const t = data?.totals || {};
  const pctDone = job && job.total ? Math.round(100 * (job.processed || 0) / job.total) : 0;
  const phaseLabel = { starting: "Iniciando…", analyzing: "Analizando conversaciones", diagnosing: "Generando diagnósticos", done: "Completado", error: "Error" }[job?.phase] || "";
  return (
    <>
      <PageHeader label="Inteligencia Conversacional" title="Dashboard del Supervisor"
        description="Calidad de los agentes IA, resolución, escalamiento y estado de recomendaciones.">
        <select data-testid="sup-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="bg-white border border-carbon-200 rounded px-3 py-2 text-sm">
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={90}>90 días</option>
          <option value={365}>1 año</option>
        </select>
        <button data-testid="sup-run-backfill" onClick={runBackfill} disabled={busy || running}
          className="bg-white border border-carbon-200 text-carbon-800 px-4 py-2 rounded text-sm flex items-center gap-2 hover:border-terracotta disabled:opacity-60">
          <ClockCounterClockwise size={14} /> {running ? "Analizando…" : "Analizar todo el histórico"}
        </button>
        <button data-testid="sup-run-diagnose" onClick={runDiagnoseAll} disabled={busy || running}
          className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
          <Stethoscope size={14} /> {busy === "diag" ? "Diagnosticando..." : "Correr diagnóstico"}
        </button>
      </PageHeader>
      <PageBody>
        {loading ? (
          <div className="text-carbon-500">Cargando...</div>
        ) : (
          <div className="space-y-8" data-testid="sup-dashboard">
            {running && (
              <Card className="p-5 border-terracotta/40 bg-terracotta-light" testid="backfill-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-terracotta flex items-center gap-2">
                    <ArrowsClockwise size={15} className="animate-spin" /> Análisis retrospectivo · {phaseLabel}
                  </span>
                  <span className="font-mono text-carbon-600">
                    {job?.phase === "analyzing" ? `${job?.processed || 0}/${job?.total || 0}` : `${job?.analyzed || 0} analizadas`}
                  </span>
                </div>
                <div className="h-2 bg-white/70 rounded-full overflow-hidden">
                  <div className="h-full bg-terracotta transition-all" style={{ width: `${job?.phase === "diagnosing" ? 100 : pctDone}%` }} />
                </div>
                <p className="text-xs text-carbon-500 mt-2">Corre en segundo plano. Puedes navegar; al terminar se generan los diagnósticos automáticamente.</p>
              </Card>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat testid="stat-total" label="Conversaciones" value={t.total_conversations} />
              <Stat testid="stat-quality" label="Calidad promedio" value={t.avg_quality_score} suffix="/100" />
              <Stat testid="stat-resolution" label="Resolución IA" value={t.ai_resolution_rate} suffix="%" />
              <Stat testid="stat-escalation" label="Escalamiento" value={t.human_escalation_rate} suffix="%" />
              <Stat label="Abandono" value={t.abandonment_rate} suffix="%" />
              <Stat label="Turnos prom." value={t.avg_turns} />
              <Stat label="Preguntas repetidas" value={t.repeat_question_rate} suffix="%" />
              <Card className="p-5">
                <div className="section-label mb-1">Recomendaciones</div>
                <div className="text-sm space-y-0.5 mt-1">
                  <div className="flex justify-between"><span className="text-carbon-500">Pendientes</span><span className="font-mono">{data?.recommendations?.PENDING_REVIEW ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-carbon-500">Implementadas</span><span className="font-mono">{data?.recommendations?.IMPLEMENTED ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-carbon-500">Rechazadas</span><span className="font-mono">{data?.recommendations?.REJECTED ?? 0}</span></div>
                </div>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h2 className="font-heading text-lg mb-3">Mejores agentes</h2>
                {(data?.best_agents || []).length === 0 ? <p className="text-sm text-carbon-500">Sin datos.</p> : (
                  <div className="space-y-2" data-testid="best-agents">
                    {data.best_agents.map((a) => (
                      <div key={a.agent_id} className="flex items-center justify-between text-sm border-b border-carbon-100 pb-2">
                        <span className="truncate">{a.agent_name || "—"}</span>
                        <span className="font-mono text-forest">{a.avg_quality}/100 · {a.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <Card className="p-6">
                <h2 className="font-heading text-lg mb-3">Agentes con oportunidades</h2>
                {(data?.worst_agents || []).length === 0 ? <p className="text-sm text-carbon-500">Sin datos.</p> : (
                  <div className="space-y-2" data-testid="worst-agents">
                    {data.worst_agents.map((a) => (
                      <div key={a.agent_id} className="flex items-center justify-between text-sm border-b border-carbon-100 pb-2">
                        <span className="truncate">{a.agent_name || "—"}</span>
                        <span className="font-mono text-signal">{a.avg_quality}/100 · {a.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <Card className="p-6">
              <h2 className="font-heading text-lg mb-3">Principales intenciones</h2>
              {(data?.top_intents || []).length === 0 ? <p className="text-sm text-carbon-500">Sin datos.</p> : (
                <div className="flex flex-wrap gap-2">
                  {data.top_intents.map((i) => (
                    <span key={i.label} className="bg-page border border-carbon-200 rounded px-3 py-1.5 text-sm">
                      {i.label} <span className="text-carbon-400 font-mono">· {i.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </Card>

            {/* Métricas de escalamiento a humano */}
            <div data-testid="escalation-metrics">
              <h2 className="font-heading text-xl mb-3">Escalamiento a humano</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat testid="esc-total" label="Escalamientos" value={data?.escalation?.total} />
                <Card className={`p-5 ${data?.escalation?.new_unattended ? "border-signal/50 bg-red-50" : ""}`} testid="esc-new">
                  <div className="section-label mb-1">Sin atender</div>
                  <div className={`font-heading text-3xl font-medium tabular-nums ${data?.escalation?.new_unattended ? "text-signal" : ""}`}>{data?.escalation?.new_unattended ?? 0}</div>
                </Card>
                <Stat testid="esc-active" label="Activos en bandeja" value={data?.escalation?.active_in_inbox} />
                <Stat testid="esc-avg" label="Score promedio" value={data?.escalation?.avg_score} suffix="/100" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="font-heading text-base mb-3">Por origen (trigger)</h3>
                  {(data?.escalation?.by_trigger || []).length === 0 ? <p className="text-sm text-carbon-500">Sin datos.</p> : (
                    <div className="space-y-2">
                      {data.escalation.by_trigger.map((b) => (
                        <div key={b.label} className="flex items-center justify-between text-sm border-b border-carbon-100 pb-1.5">
                          <span>{b.label === "EXPLICIT_REQUEST" ? "Pidió una persona" : b.label === "SCORE" ? "Por score de riesgo" : b.label}</span>
                          <span className="font-mono">{b.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
                <Card className="p-6">
                  <h3 className="font-heading text-base mb-3">Principales señales</h3>
                  {(data?.escalation?.top_signals || []).length === 0 ? <p className="text-sm text-carbon-500">Sin datos.</p> : (
                    <div className="flex flex-wrap gap-2">
                      {data.escalation.top_signals.map((s) => (
                        <span key={s.label} className="bg-page border border-carbon-200 rounded px-3 py-1.5 text-xs font-mono">
                          {s.label} <span className="text-carbon-400">· {s.count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
